import _extends from "/usr/app/node_modules/@babel/runtime/helpers/esm/extends.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import loadable from "@loadable/component";
import { SHOW_DEBUG_INFO } from "@globalConfig";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import { useAuthActions, useSession } from "@/_MiniApps/auth/AuthContext";
import { useCart } from "@/common/Contexts/CartContext";
import useHelpers from "@hooks/useHelpers";
import useIsBrowser from "@hooks/useIsBrowser";
import useIsScrolled from "@hooks/useIsScrolled";
import { trackEvents } from "@/common/events/events";
import { getPlatformInfo } from "@/utils/GlobalJavascriptFunction";
import SyarahLogo from "@/components/SyarahLogo/SyarahLogo";
import BuildInfo from "../BuildInfo";
import { cn } from "@/common/lib/utils";
import useNavItemTracker, { navItemName } from "../navEvents";
import { SHOW_NOTIFICATION_CENTER } from "../navFeatures";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import desktopNavData from "./desktopNavData";
var UnbxdTextSearch = loadable({
  resolved: {},
  chunkName: function chunkName() {
    return "components-SearchComponent-UnbxdComponents-UnbxdAsideFilters-components-UnbxdTextSearch-UnbxdTextSearch";
  },
  isReady: function isReady(props) {
    var key = this.resolve(props);
    if (this.resolved[key] !== true) {
      return false;
    }
    if (typeof __webpack_modules__ !== 'undefined') {
      return !!__webpack_modules__[key];
    }
    return false;
  },
  importAsync: function importAsync() {
    return import(/* webpackChunkName: "components-SearchComponent-UnbxdComponents-UnbxdAsideFilters-components-UnbxdTextSearch-UnbxdTextSearch" */"../../../../components/SearchComponent/UnbxdComponents/UnbxdAsideFilters/components/UnbxdTextSearch/UnbxdTextSearch");
  },
  requireAsync: function requireAsync(props) {
    var _this = this;
    var key = this.resolve(props);
    this.resolved[key] = false;
    return this.importAsync(props).then(function (resolved) {
      _this.resolved[key] = true;
      return resolved;
    });
  },
  requireSync: function requireSync(props) {
    var id = this.resolve(props);
    if (typeof __webpack_require__ !== 'undefined') {
      return __webpack_require__(id);
    }
    return eval('module.require')(id);
  },
  resolve: function resolve() {
    if (require.resolveWeak) {
      return require.resolveWeak("../../../../components/SearchComponent/UnbxdComponents/UnbxdAsideFilters/components/UnbxdTextSearch/UnbxdTextSearch");
    }
    return eval('require.resolve')("../../../../components/SearchComponent/UnbxdComponents/UnbxdAsideFilters/components/UnbxdTextSearch/UnbxdTextSearch");
  }
}, {
  fallback: /*#__PURE__*/React.createElement("div", {
    className: "h-12 w-[366px] rounded-full border border-solid border-[#e6e9ef] max-[1280px]:w-[280px]"
  })
});
/** Shared shell for both حسابي states — Figma 4118:21750 / 4118:21749. */
var accountPillClass = "h-[37px] gap-1.5 rounded-full border-[0.75px] border-solid border-[#e6e9ef] bg-[#fbfbfb] py-0.5 pe-1.5 ps-0.5 text-navy-primary shadow-[inset_0_-1px_1px_0_rgba(150,161,172,0.12)]";

/**
 * Hover treatment for the row-2 items themselves (the two category triggers and
 * the flat links) — not for the rows inside their panels.
 *
 * A muted surface rather than a weight change: `hover:font-bold` reflowed the
 * row on every hover, which is what `NavLabel` used to exist to absorb.
 *
 * `bg-muted` is not a utility in this project — `muted` is not in
 * tailwind.config, so it compiles to nothing at all. `blue-alice` (#f1f6fc) is
 * the surface the dropdown rows and the drawer's login card already hover to,
 * so the nav keeps one hover language.
 *
 * `text-black` is likewise invalid here: the config redefines `black` as
 * `{ primary }` with no DEFAULT, so the token is `text-black-primary`.
 *
 * The padding is what makes the surface a target; `gap-0` plus `px-3` on each
 * item reproduces the 24px Figma gap between labels exactly (12 + 12), and the
 * list's `-ms-3` pulls the first label back flush with the container edge.
 */
var topLevelHoverClass = cn("rounded-lg px-3 py-1.5 transition-colors", "hover:bg-blue-alice hover:text-black-primary", "data-[state=open]:bg-blue-alice data-[state=open]:text-black-primary");

/**
 * Row 2's collapse/reveal duration. Mirrors the `duration-300` utility on the
 * wrapper — the two have to stay in step, hence the constant next to it.
 */
var ROW_TRANSITION_MS = 300;

/** Row 1: 15 top + 56 content + 18 bottom. The lite header is this alone. */
var ROW_ONE_HEIGHT = 89;
/** Row 2: 1px rule + 10 + 57 row + 12 tail. Removed from flow once scrolled. */
var ROW_TWO_HEIGHT = 80;
var megaPanelSurface = "border border-solid border-[#f0f2f7] bg-white shadow-[0_8px_30px_0_rgba(0,20,60,0.12)]";
var AccountAvatar = function AccountAvatar() {
  return /*#__PURE__*/React.createElement("span", {
    className: "inline-flex size-8 items-center justify-center rounded-full border border-solid border-white bg-violet-light shadow-[0_0.5px_1px_0_rgba(74,58,255,0.07)]"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: "iconify material-symbols--person-outline-rounded text-violet",
    style: {
      width: 16,
      height: 16
    }
  }));
};

/**
 * Pre-hydration stand-in for the account pill — SE-13687.
 *
 * `isLoggedIn` comes out of localStorage, which the server cannot read, so the
 * SSR markup is always the logged-out pill. For a logged-in visitor the client
 * then swaps "تسجيل الدخول" for "حسابي" once it mounts, and that swap is the
 * flicker. Rendering this on both sides means neither label is shown before the
 * real state is known.
 *
 * One flat grey pill — no border, no inner blocks. An outlined shell with
 * shapes inside reads as a third state on the way to the real thing, and it
 * arrived in pieces: the border is a Tailwind utility in the critical CSS, while
 * anything relying on a CSS module (`BoxLoading`) had no colour until its
 * stylesheet chunk landed. Inline `style` and a single fill mean the whole
 * placeholder is there in the first paint or not at all.
 *
 * It keeps `accountPillClass`'s footprint (37px tall, pill radius); 112px sits
 * between the two real widths (~128px logged out, ~97px logged in), so the swap
 * moves the neighbouring icons by a few pixels at most.
 *
 * `pointerEvents: none` is deliberate: the pre-hydration click catcher in
 * script_loader_renderer.ts records the nearest ancestor `data-id` and replays a
 * click on it once React is live. Letting clicks fall through to the plain
 * wrapper resolves to no selector, so nothing is replayed onto a placeholder
 * that no longer exists.
 */
var AccountPillSkeleton = function AccountPillSkeleton() {
  return /*#__PURE__*/React.createElement("div", {
    "aria-hidden": "true"
    // Not `data-id` — that attribute is what the click catcher replays against.
    // This one is only a marker, so the SSR HTML can be checked for it:
    //   curl -s localhost:3000/ | grep -c 'data-nav-account="loading"'
    // A miss there means the served HTML predates this component.
    ,
    "data-nav-account": "loading",
    style: {
      width: 112,
      height: 37,
      borderRadius: 9999,
      backgroundColor: "#e9edf3",
      pointerEvents: "none"
    }
  });
};
var iconBoxClass = "inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-solid border-[#e6e9ef] text-navy-primary transition-colors hover:border-primary-new/40 hover:text-primary-new";

/**
 * One row inside a dropdown panel. Reading order in RTL is: icon, label, then a
 * trailing arrow pinned to the far edge — mirrored automatically in English.
 *
 * `mega` is the two-column browse/discover layout (37px icon well, 24px arrow);
 * `account` is the narrow single-column panel (30px icon, 20px caret).
 */
var MegaMenuItem = function MegaMenuItem(_ref) {
  var _link$iconSize, _link$iconSize2, _link$iconSize3, _link$iconSize4;
  var link = _ref.link,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "mega" : _ref$variant,
    group = _ref.group;
  var track = useNavItemTracker("top_navigation");
  var isAccount = variant === "account";
  var isDanger = link.tone === "danger";
  // Logout has no URL — it renders as a real button rather than a dead anchor.
  var isAction = Boolean(link.onSelect) && link.href === "#";
  /* The mega rows carry no trailing arrow: with two columns of five, twelve
     repeated arrows added noise without telling the reader anything the row
     did not already say. The narrow account panel keeps its chevron, where a
     single column reads as a drill-in list.
     Keyed off `isAction`, not `onSelect`: account rows such as "تتبع طلباتي"
     carry an `onSelect` for their own analytics event and still navigate, so
     they keep the chevron. Only the destination-less logout row loses it. */
  var showChevron = isAccount && !isAction;
  var Row = isAction ? "button" : "a";
  var onClick = function onClick() {
    var _link$onSelect;
    track(navItemName(link.id), group);
    // Logout's own handler still runs; the event is additive.
    (_link$onSelect = link.onSelect) === null || _link$onSelect === void 0 ? void 0 : _link$onSelect.call(link);
  };
  var rowProps = isAction ? {
    type: "button",
    onClick: onClick
  } : {
    href: link.href,
    target: link.isExternal ? "_blank" : undefined,
    rel: link.isExternal ? "noopener noreferrer" : undefined,
    onClick: onClick
  };
  return /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement(NavigationMenuLink, {
    asChild: true
  }, /*#__PURE__*/React.createElement(Row, _extends({}, rowProps, {
    id: link.id
    // `data-id` is what the pre-hydration click catcher in
    // script_loader_renderer.ts looks for when it replays the first click.
    // It only scans 2 levels up for an `id`, and the click target here is a
    // nested <span>, so without this the click is swallowed and never
    // replayed. See getClickedSelector().
    ,
    "data-id": link.id,
    className: cn("group/item flex w-full cursor-pointer flex-row items-center gap-2.5", "rounded-lg border-0 bg-transparent text-start no-underline transition-colors", isDanger ? "hover:bg-[#fdecec]" : "hover:bg-blue-alice", isAccount ? "px-2 py-1" : "px-3 py-2")
  }), /*#__PURE__*/React.createElement("span", {
    className: "flex shrink-0 items-center justify-center",
    style: {
      width: isAccount ? 30 : 28,
      height: isAccount ? 30 : 28
    }
  }, link.IconComponent ? /*#__PURE__*/React.createElement(link.IconComponent, {
    "aria-hidden": "true",
    width: (_link$iconSize = link.iconSize) !== null && _link$iconSize !== void 0 ? _link$iconSize : 20,
    height: (_link$iconSize2 = link.iconSize) !== null && _link$iconSize2 !== void 0 ? _link$iconSize2 : 20,
    className: cn(isDanger ? "text-[#f22c2c]" : "text-navy-primary group-hover/item:text-primary-new")
  }) : /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: cn("iconify", link.icon, isDanger ? "text-[#f22c2c]" : "text-navy-primary group-hover/item:text-primary-new"),
    style: {
      width: (_link$iconSize3 = link.iconSize) !== null && _link$iconSize3 !== void 0 ? _link$iconSize3 : 20,
      height: (_link$iconSize4 = link.iconSize) !== null && _link$iconSize4 !== void 0 ? _link$iconSize4 : 20
    }
  })), /*#__PURE__*/React.createElement("span", {
    className: cn("min-w-0 flex-1", isDanger ? "text-base font-normal text-[#f22c2c]" : "font-medium text-navy-primary group-hover/item:text-primary-new", !isDanger && (isAccount ? "text-sm" : "text-[13px] leading-5"))
  }, link.title), showChevron && /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: cn("iconify material-symbols--chevron-left-rounded shrink-0 en:rotate-180", isDanger ? "text-[#f22c2c]" : "text-navy-primary group-hover/item:text-primary-new"),
    style: {
      width: 20,
      height: 20
    }
  }))));
};

/** A dropdown category (حسابي / تصفح في سيارة / تعرف على سيارة). */
var MegaMenu = function MegaMenu(_ref2) {
  var group = _ref2.group,
    _ref2$align = _ref2.align,
    align = _ref2$align === void 0 ? "start" : _ref2$align,
    eventGroup = _ref2.eventGroup;
  return /*#__PURE__*/React.createElement(NavigationMenuItem, {
    value: group.key
  }, /*#__PURE__*/React.createElement(NavigationMenuTrigger, {
    id: "nav-trigger-".concat(group.key),
    "data-id": "nav-trigger-".concat(group.key),
    className: cn("gap-1 text-base", topLevelHoverClass)
  }, group.title, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
    // Figma uses arrow-forward-ios rotated 90deg (a thin chevron), not
    // keyboard-arrow-down. Open state points it back up.
    ,
    className: cn("iconify material-symbols--arrow-forward-ios-rounded shrink-0 rotate-90",
    // 300ms, not the 150ms `transition-transform` ships with: the flip
    // happens on hover, so at the default speed it snaps.
    "transition-transform duration-300 ease-out motion-reduce:transition-none", "group-data-[state=open]:-rotate-90"),
    style: {
      width: 16,
      height: 16
    }
  })), /*#__PURE__*/React.createElement(NavigationMenuContent, {
    className: cn(megaPanelSurface, align === "end" ? "end-0" : "start-0", "absolute mt-3 rounded-2xl p-3"),
    style: {
      width: group.width
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-row gap-2"
  }, group.columns.map(function (column, index) {
    return /*#__PURE__*/React.createElement("ul", {
      key: index,
      className: "m-0 flex list-none flex-col gap-0.5 p-0",
      style: {
        flex: 1
      }
    }, column.map(function (link) {
      var _link$id;
      return /*#__PURE__*/React.createElement(MegaMenuItem, {
        key: (_link$id = link.id) !== null && _link$id !== void 0 ? _link$id : link.href,
        link: link,
        group: eventGroup
      });
    }));
  }))));
};
var DesktopHeader = function DesktopHeader(_ref3) {
  var variant = _ref3.variant,
    showSearchBar = _ref3.showSearchBar,
    setUnbxdPage = _ref3.setUnbxdPage,
    switchLink = _ref3.switchLink,
    logoNotClickable = _ref3.logoNotClickable,
    showCartIcon = _ref3.showCartIcon;
  var _useTranslation = useTranslation(["web_header", "web_aside_menu", "web_common"]),
    t = _useTranslation.t,
    tShort = _useTranslation.tShort;
  var lang = useLang();
  var location = useLocation();
  var _useHelpers = useHelpers(),
    createUrl = _useHelpers.createUrl,
    createUrl2 = _useHelpers.createUrl2;
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn;
  /* The account pill is the one part of the header whose state the server
     cannot know, so it waits for the client rather than guessing — SE-13687. */
  var isBrowser = useIsBrowser();
  var _useAuthActions = useAuthActions(),
    logout = _useAuthActions.logout;
  var _useCart = useCart(),
    totalItems = _useCart.totalItems;
  /**
   * Cart visibility, same rule as the pre-revamp header: the page has to ask
   * for it, and there has to be something in it. `isBrowser` is part of the
   * test because the count is only known after hydration
   * (`cartCountFromMeta` starts at 0), so server and first client render agree
   * on hidden and the icon arrives with its badge.
   */
  var hasCartItems = Boolean(showCartIcon) && isBrowser && totalItems > 0;
  var trackNav = useNavItemTracker("top_navigation");
  var _useState = useState(false),
    isSearchExpanded = _useState[0],
    setIsSearchExpanded = _useState[1];
  /* Scrolling down collapses the header to row 1 — Figma 4009:25938.
     Collapsing removes 80px of in-flow height, so the two thresholds are kept
     100px apart: anything narrower and scroll anchoring's compensating jump
     re-crosses the boundary and the header flickers. */
  var isScrolled = useIsScrolled(120, 20);

  /*
   * True once row 2 has finished opening, which is when its overflow can go
   * back to `visible`.
   *
   * A height transition only reads as a slide if the box clips, but that same
   * clip cuts off the mega-menu panels, which hang below the header when a
   * dropdown is open. No panel can be open while row 2 is collapsed, so the
   * clip only needs lifting after the row has settled open.
   *
   * Driven by a timer rather than `transitionend`: under
   * `prefers-reduced-motion` there is no transition, so that event would never
   * fire and the panels would stay clipped for good.
   */
  var _useState2 = useState(true),
    isRowSettled = _useState2[0],
    setIsRowSettled = _useState2[1];
  useEffect(function () {
    if (isScrolled) {
      setIsRowSettled(false);
      return;
    }
    var timer = setTimeout(function () {
      return setIsRowSettled(true);
    }, ROW_TRANSITION_MS + 20);
    return function () {
      return clearTimeout(timer);
    };
  }, [isScrolled]);
  var nav = desktopNavData({
    t: t,
    lang: lang,
    createUrl: createUrl,
    isLoggedIn: isLoggedIn,
    logout: logout
  });
  var isLite = variant === "lite";

  /*
   * Publishes the header's in-flow height as `--desktop-header-height`.
   *
   * Anything sticky below the header has to know where the header ends, and
   * that number is no longer a constant: it is 89 for the lite header and for
   * the full one after row 2 collapses, but 169 at the top of a full page.
   * Components that hardcoded the old 70px shell (the search aside, for one)
   * ended up either tucked under the header or hanging past the viewport
   * bottom, taking their bottom-pinned controls off screen with them.
   *
   * Written to `documentElement` rather than passed down because the consumers
   * are CSS modules in unrelated trees. This component is mounted but hidden
   * under 1025px (`#desktopNav.m-hide`), hence the `desktop` in the name — the
   * responsive layouts must not read it.
   */
  useEffect(function () {
    document.documentElement.style.setProperty("--desktop-header-height", "".concat(isLite || isScrolled ? ROW_ONE_HEIGHT : ROW_ONE_HEIGHT + ROW_TWO_HEIGHT, "px"));
  }, [isLite, isScrolled]);
  var langSwitchHref = // `keepQuery`: the switcher has to land on the same page, and some pages
  // are identified only by a query param (order_id on the araboon checkout).
  switchLink !== null && switchLink !== void 0 ? switchLink : createUrl2(location.pathname + location.search, {}, true);
  var isSearchOpen = Boolean(showSearchBar && isSearchExpanded);
  return /*#__PURE__*/React.createElement("div", {
    className: "relative bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.08)]"
  }, showSearchBar && /*#__PURE__*/React.createElement("div", {
    "aria-hidden": "true",
    className: cn("absolute inset-x-0 top-full z-20 bg-[rgba(0,0,0,0.5)] transition-opacity duration-200", isSearchOpen ? "h-screen opacity-100" : "pointer-events-none h-0 opacity-0")
  }), /*#__PURE__*/React.createElement("div", {
    className: "container relative flex flex-col"
  }, /*#__PURE__*/React.createElement("div", {
    className: "mb-[18px] mt-[15px] flex h-14 items-center justify-between gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-1 items-center gap-6"
  }, /*#__PURE__*/React.createElement("a", {
    href: createUrl("/"),
    "aria-label": "Syarah home",
    "data-id": "nav-logo",
    onClick: function onClick() {
      return trackNav("logo");
    },
    className: cn("shrink-0", logoNotClickable && "pointer-events-none")
  }, /*#__PURE__*/React.createElement(SyarahLogo, {
    width: 83,
    height: 38,
    id: "home-logo",
    className: "hasEvents"
  })), showSearchBar && /*#__PURE__*/React.createElement("div", {
    "data-expanded": isSearchOpen || undefined,
    className: cn("group/search min-w-0 shrink grow-0 basis-[366px] transition-colors duration-200", "max-[1280px]:basis-[280px]",
    // Focused: cover the whole header surface so the input can
    // stretch edge to edge and the results drop beneath it.
    "data-[expanded]:absolute data-[expanded]:inset-0 data-[expanded]:z-30", "data-[expanded]:flex data-[expanded]:flex-col", "data-[expanded]:justify-start data-[expanded]:bg-white data-[expanded]:pt-6")
  }, /*#__PURE__*/React.createElement(UnbxdTextSearch, {
    setPage: setUnbxdPage,
    referral_location: "listing",
    variant: "navbar"
    // `!` is required: these fight `.wrapper` and `.navbarWrapper`
    // in UnbxdTextSearch.module.css, which a plain utility loses to.
    ,
    customClass: cn("!w-full !max-w-[366px] max-[1280px]:!max-w-[280px]", "group-data-[expanded]/search:!max-w-none group-data-[expanded]/search:!rounded-full", "group-data-[expanded]/search:!px-6 group-data-[expanded]/search:!py-[13px]"),
    onFocusChange: setIsSearchExpanded
  }))), /*#__PURE__*/React.createElement("div", {
    className: "flex shrink-0 items-center gap-3"
  }, /*#__PURE__*/React.createElement("a", {
    href: langSwitchHref,
    className: cn("inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-full border border-solid border-[#e6e9ef] px-2 no-underline", "text-sm tracking-[-0.28px] text-navy-primary transition-colors hover:border-primary-new/40 hover:text-primary-new"),
    id: "nav-lang-switch",
    "data-id": "nav-lang-switch",
    onClick: function onClick() {
      return trackNav("language_switch");
    }
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: "iconify material-symbols--language shrink-0",
    style: {
      width: 17,
      height: 17
    }
  }), /*#__PURE__*/React.createElement("span", null, tShort("switch_language", {
    ns: "web_common"
  }))), SHOW_NOTIFICATION_CENTER && /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-label": t("notifications", {
      ns: "web_header"
    }),
    className: cn(iconBoxClass, "cursor-pointer bg-transparent p-0"),
    id: "nav-notifications",
    "data-id": "nav-notifications",
    onClick: function onClick() {
      return trackNav("notifications");
    }
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: "iconify material-symbols--notifications-outline-rounded",
    style: {
      width: 18,
      height: 18
    }
  })), hasCartItems && /*#__PURE__*/React.createElement("a", {
    href: createUrl("/site/cart"),
    "aria-label": t("cart", {
      ns: "web_header"
    }),
    className: cn(iconBoxClass, "relative no-underline"),
    id: "nav-cart",
    "data-id": "nav-cart",
    onClick: function onClick(e) {
      trackNav("cart");
      // Already on the cart: swallow the navigation rather than
      // reloading the page under the user. Carried over from the
      // old header's CartBadgeIcon, and matches the responsive one.
      if (__IS_CSR__ && window.location.pathname === new URL(createUrl("/site/cart"), window.location.origin).pathname) {
        e.preventDefault();
      }
    }
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: "iconify material-symbols--shopping-cart-outline-rounded",
    style: {
      width: 16,
      height: 16
    }
  }), /*#__PURE__*/React.createElement("span", {
    className: "absolute -top-1 -start-1 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[#EC6234] px-[3px] text-[9px] font-bold leading-none text-white"
  }, totalItems > 99 ? "99+" : totalItems)), !isBrowser ? /*#__PURE__*/React.createElement(AccountPillSkeleton, null) : isLoggedIn ? /*#__PURE__*/React.createElement(NavigationMenu, {
    dir: lang === "ar" ? "rtl" : "ltr",
    viewport: false,
    delayDuration: 100,
    className: "max-w-none flex-none"
  }, /*#__PURE__*/React.createElement(NavigationMenuList, {
    className: "gap-0"
  }, /*#__PURE__*/React.createElement(NavigationMenuItem, {
    value: nav.account.key
  }, /*#__PURE__*/React.createElement(NavigationMenuTrigger, {
    id: "nav-trigger-account",
    "data-id": "nav-trigger-account",
    className: cn(accountPillClass, "w-auto")
  }, /*#__PURE__*/React.createElement(AccountAvatar, null), /*#__PURE__*/React.createElement("span", {
    className: "flex items-center gap-0.5"
  }, /*#__PURE__*/React.createElement("span", {
    className: "whitespace-nowrap text-xs font-semibold leading-[1.35]"
  }, nav.account.title), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: cn("iconify material-symbols--keyboard-arrow-down-rounded shrink-0", "transition-transform duration-300 ease-out motion-reduce:transition-none", "group-data-[state=open]:rotate-180"),
    style: {
      width: 14,
      height: 14
    }
  }))), /*#__PURE__*/React.createElement(NavigationMenuContent, {
    className: cn(megaPanelSurface, "absolute end-0 mt-2 rounded-xl p-4"),
    style: {
      width: nav.account.width
    }
  }, /*#__PURE__*/React.createElement("ul", {
    className: "m-0 flex list-none flex-col gap-2 p-0"
  }, nav.account.columns[0].map(function (link) {
    var _link$id2;
    return /*#__PURE__*/React.createElement(MegaMenuItem, {
      key: (_link$id2 = link.id) !== null && _link$id2 !== void 0 ? _link$id2 : link.href,
      link: link,
      variant: "account",
      group: "my_account"
    });
  })), SHOW_DEBUG_INFO && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "mt-2 h-px w-full bg-[#e6e9ef]"
  }), /*#__PURE__*/React.createElement(BuildInfo, {
    className: "mt-2"
  })))))) :
  /*#__PURE__*/
  /* Logged out the pill is a plain link to login, not a dropdown —
     Figma 4118:21749: 128px wide, chevron points to the page. */
  React.createElement("a", {
    id: "nav-login",
    "data-id": "nav-login",
    href: nav.loginHref,
    onClick: function onClick() {
      return trackNav("login", "my_account");
    },
    className: cn(accountPillClass, "inline-flex w-auto items-center no-underline", "transition-colors hover:border-primary-new/40")
  }, /*#__PURE__*/React.createElement(AccountAvatar, null), /*#__PURE__*/React.createElement("span", {
    className: "flex items-center gap-0.5"
  }, /*#__PURE__*/React.createElement("span", {
    className: "whitespace-nowrap text-xs font-semibold leading-[1.35]"
  }, t("login", {
    ns: "web_header"
  })), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: "iconify material-symbols--chevron-left-rounded shrink-0 en:rotate-180",
    style: {
      width: 14,
      height: 14
    }
  }))))), !isLite &&
  /*#__PURE__*/
  /* Row 2 and its divider slide away once the page scrolls and slide
     back on the way up. The animated height is the band's own 80px
     (1 + 10 + 57 + 12), so nothing is measured at runtime.
      `pb-0` while collapsed is not cosmetic: with the global
     `box-sizing: border-box`, a `height: 0` box still cannot shrink
     below its own padding, so leaving the 12px on would strand a 12px
     gap and put the collapsed header at 101px instead of 89px.
      `visibility` is in the transition list deliberately. On the way out
     it is discrete and flips only at the end, so the row fades out
     completely before it leaves the tab order; on the way in it flips
     at once. That keeps keyboard focus out of a collapsed row without a
     second timer.
      Kept in the tree (not unmounted) so the markup React hydrates is
     the markup the server sent — `isScrolled` is false on both. */
  React.createElement("div", {
    className: cn("box-border pb-[12px]", "transition-[height,opacity,visibility] duration-300 ease-out", "motion-reduce:transition-none", isScrolled ? "invisible h-0 pb-0 opacity-0" : "h-[80px] opacity-100", isRowSettled ? "overflow-visible" : "overflow-hidden")
  }, /*#__PURE__*/React.createElement("div", {
    className: "mb-[10px] h-px w-full bg-[#e6e9ef]"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex h-[57px] items-center justify-between gap-6"
  }, /*#__PURE__*/React.createElement(NavigationMenu, {
    dir: lang === "ar" ? "rtl" : "ltr",
    viewport: false,
    delayDuration: 100,
    className: "max-w-none flex-1 justify-start"
  }, /*#__PURE__*/React.createElement(NavigationMenuList, {
    className: "-ms-3 flex-wrap justify-start gap-0"
  }, /*#__PURE__*/React.createElement(MegaMenu, {
    group: nav.browse,
    align: "start",
    eventGroup: "browse_syarah"
  }), /*#__PURE__*/React.createElement(MegaMenu, {
    group: nav.discover,
    align: "start",
    eventGroup: "discover_syarah"
  }), nav.mainLinks.map(function (link) {
    var _link$id3;
    return /*#__PURE__*/React.createElement(NavigationMenuItem, {
      key: (_link$id3 = link.id) !== null && _link$id3 !== void 0 ? _link$id3 : link.href
    }, /*#__PURE__*/React.createElement(NavigationMenuLink, {
      asChild: true
    }, /*#__PURE__*/React.createElement("a", {
      id: link.id,
      "data-id": link.id,
      href: link.href,
      onClick: function onClick() {
        var _link$onSelect2;
        trackNav(navItemName(link.id));
        // Row-2 links are rendered here rather than through
        // MegaMenuItem, so their own handler has to be run
        // explicitly — this is what carries
        // `order_tracking_entry` for "تعقب طلباتي".
        (_link$onSelect2 = link.onSelect) === null || _link$onSelect2 === void 0 ? void 0 : _link$onSelect2.call(link);
      },
      className: cn("text-base font-medium tracking-[-0.32px] text-navy-primary no-underline", topLevelHoverClass)
    }, link.title)));
  }))), /*#__PURE__*/React.createElement("a", {
    id: "nav-sell-car-cta",
    "data-id": "nav-sell-car-cta",
    href: nav.sellCarHref,
    onClick: function onClick() {
      trackNav("sell_your_car");
      trackEvents("sell_car_started", {
        referral_location: "top_navigation_cta",
        platform: getPlatformInfo(),
        version: "3"
      });
    },
    className: "inline-flex h-[46px] w-[174px] shrink-0 items-center justify-center rounded-full border border-solid border-primary-new text-base font-bold tracking-[-0.32px] text-primary-new no-underline transition-colors hover:bg-primary-new hover:text-white max-[1280px]:w-[150px]"
  }, t("sell_car", {
    ns: "web_aside_menu"
  }))))));
};
export default DesktopHeader;